import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ApiError } from './api.ts';
import { NavProvider, useNav, type Drawer } from './app/nav.tsx';
import { ToastProvider } from './app/toast.tsx';
import { InstalarSheet, OffBar, registerSW } from './app/pwa.tsx';
import { TourHost } from './app/tour.tsx';
import { can, queryClient, useMe } from './lib/data.ts';
import { Agenda } from './screens/Agenda.tsx';
import { AbrirCaixaSheet, EditarAberturaSheet, Caixa, FecharSheet, MovSheet } from './screens/Caixa.tsx';
import { EstornoSheet } from './sheets/EstornoSheet.tsx';
import { Cliente } from './screens/Cliente.tsx';
import { Clientes } from './screens/Clientes.tsx';
import { Avaliacoes, AvisosSheet, Lembretes, ListaEspera, Mensagens, MeuLink, RetornoSheet } from './screens/Engage.tsx';
import { Hoje } from './screens/Hoje.tsx';
import { DespesaSheet, Despesas, Relatorios } from './screens/Reports.tsx';
import { CarrinhoSheet, Fidelidade, PacoteSheet, Pacotes, ProdutoSheet, Produtos, ValeSheet, Vales, Vendas } from './screens/Store.tsx';
import { FotosSheet, JuntarSheet } from './sheets/ClientExtras.tsx';
import { Login, NovaSenha } from './screens/Login.tsx';
import { Mais } from './screens/Mais.tsx';
import { Pendencias, Repasse } from './screens/Money.tsx';
import { AnamPub, Avaliar, MeuHorario, PubBooking } from './screens/Public.tsx';
import { Acessos, ConviteSheet, MembroSheet, NovoPerfilSheet, PerfilAcesso } from './screens/Acessos.tsx';
import { Tabela } from './screens/Tabela.tsx';
import { BloqueioSheet, Equipe, ModelosSheet, Parceria, Servico, Servicos } from './screens/Catalog.tsx';
import { Folgas, Historico, Horarios, Maquininha, Pagamentos, Perfil, Regras, Salao, Setup, SetupNav } from './screens/Config.tsx';
import { ApptSheet } from './sheets/ApptSheet.tsx';
import { AnamneseSheet, DividaSheet, EditCliSheet } from './sheets/ClientSheets.tsx';
import { ComandaSheet } from './sheets/ComandaSheet.tsx';
import { JuntasSheet } from './sheets/JuntasSheet.tsx';
import { NovoSheet } from './sheets/NovoSheet.tsx';
import { TemHorarioSheet } from './sheets/TemHorarioSheet.tsx';
import { CadeiraMode } from './sheets/CadeiraMode.tsx';
import { Icon } from './ui/Icon.tsx';
import { Boundary } from './ui/State.tsx';
import { initTheme } from './lib/theme.ts';
// fonte servida pelo próprio app (sem Google): nenhum IP de cliente sai para terceiros
import '@fontsource-variable/jost/wght.css';
import '@fontsource-variable/figtree/wght.css';
import './tokens.css';
import './design.css';
import './app.css';
import './agenda.css';
import './layout.css';
import './reports.css';
import './dark.css';
import { CFG_VIEWS, SideNav, useWide } from './app/desk.tsx';
import { useLiveSync } from './lib/live.ts';

// com o app aberto, mudanças feitas em outro aparelho chegam aqui na hora
function LiveSync() { useLiveSync(); return null; }

function Shell() {
  const me = useMe();
  if (me.isLoading) return <p className="m-hint" style={{ padding: 24 }}>Abrindo…</p>;
  // só pede login quando a sessão acabou de verdade (401); outro erro é falha de conexão
  const expired = me.error instanceof ApiError && me.error.status === 401;
  if (!me.data && (expired || !me.error)) return <main><Login onDone={() => me.refetch()} /></main>;
  if (!me.data) return <div className="empty-box" style={{ margin: 24 }}><b>Sem conexão com o salão</b><p>Confira a internet. Nada foi perdido.</p><div className="empty-acts"><button className="cta" onClick={() => me.refetch()}>Tentar de novo</button></div></div>;
  return (
    <NavProvider>
      <LiveSync />
      <Frame />
      <DrawerHost />
      <TourHost />
    </NavProvider>
  );
}

// celular: tela + menu embaixo · computador: menu lateral + tela larga
function Frame() {
  const wide = useWide();
  if (!wide) return <><OffBar /><main id="conteudo"><Screen /></main><BottomNav /></>;
  return <div className="desk"><SideNav /><main className="desk-main"><OffBar /><Screen wide /></main></div>;
}

function Screen({ wide = false }: { wide?: boolean }) {
  const nav = useNav();
  // no computador, as configurações ficam com a lista de seções ao lado
  if (wide && CFG_VIEWS.has(nav.view)) {
    return (
      <div className="desk-split cfg-split">
        <div className="desk-list"><SetupNav active={nav.view} /></div>
        <div className="desk-detail cfg-detail" key={nav.view + (nav.params.id ?? '')}><Boundary resetKey={nav.view}>{nav.view === 'setup' ? <Salao /> : <Section />}</Boundary></div>
      </div>
    );
  }
  return <Boundary resetKey={nav.view + (nav.params.id ?? '')}><Section wide={wide} /></Boundary>;
}

function Section({ wide = false }: { wide?: boolean }) {
  const nav = useNav();
  switch (nav.view) {
    case 'agenda': return <Agenda key={nav.params.date ?? ''} />;
    // no computador, lista e ficha lado a lado
    case 'clientes': case 'cliente':
      // sem ficha aberta, a tabela ocupa a largura toda; com ficha, a lista encolhe para o lado
      if (wide && nav.view === 'clientes') return <Clientes mode="table" key={nav.params.filter ?? ''} />;
      if (wide) return <div className="desk-split"><div className="desk-list"><Clientes mode="list" key={nav.params.filter ?? ''} /></div><div className="desk-detail"><Cliente key={nav.params.id} /></div></div>;
      return nav.view === 'cliente' ? <Cliente key={nav.params.id} /> : <Clientes key={nav.params.filter ?? ''} />;
    case 'caixa': return <Caixa />;
    case 'mais': return <Mais />;
    case 'pendencias': return <Pendencias />;
    case 'repasse': return <Repasse />;
    case 'setup': return <Setup />;
    case 'salao': return <Salao />;
    case 'horarios': return <Horarios />;
    case 'folgas': return <Folgas key={nav.params.id} />;
    case 'regras': return <Regras />;
    case 'pagamentos': return <Pagamentos />;
    case 'maquininha': return <Maquininha key={nav.params.id} />;
    case 'tabela': return <Tabela />;
    case 'servicos': return <Servicos />;
    case 'servico': return <Servico key={nav.params.id} />;
    case 'equipe': return <Equipe />;
    case 'parceria': return <Parceria key={nav.params.id} />;
    case 'acessos': return <Acessos />;
    case 'perfilAcesso': return <PerfilAcesso key={nav.params.id} />;
    case 'perfil': return <Perfil />;
    case 'historico': return <Historico />;
    case 'mensagens': return <Mensagens />;
    case 'lembretes': return <Lembretes key={nav.params.date ?? ''} />;
    case 'meuLink': return <MeuLink />;
    case 'espera': return <ListaEspera key={(nav.params.date ?? '') + (nav.params.time ?? '')} />;
    case 'avaliacoes': return <Avaliacoes />;
    case 'relatorios': return <Relatorios />;
    case 'despesas': return <Despesas key={nav.params.month ?? ''} />;
    case 'vendas': return <Vendas />;
    case 'produtos': return <Produtos />;
    case 'pacotes': return <Pacotes />;
    case 'vales': return <Vales />;
    case 'fidelidade': return <Fidelidade />;
    default: return <Hoje />;
  }
}

function BottomNav() {
  const nav = useNav();
  const me = useMe().data!;
  const groups: Record<string, string> = { hoje: 'hoje', agenda: 'agenda', clientes: 'clientes', cliente: 'clientes', caixa: 'caixa' };
  const active = groups[nav.view] ?? 'mais';
  const btn = (k: 'hoje' | 'agenda' | 'clientes' | 'caixa' | 'mais', icon: Parameters<typeof Icon>[0]['name'], label: string) => (
    <button className={active === k ? 'active' : ''} aria-current={active === k ? 'page' : 'false'} onClick={() => nav.go(k)}><Icon name={icon} />{label}</button>
  );
  return (
    <nav className="mobile-nav" aria-label="Navegação principal">
      {btn('hoje', 'home', 'Hoje')}{btn('agenda', 'cal', 'Agenda')}{btn('clientes', 'users', 'Clientes')}
      {can(me, 'valoresSalao') && btn('caixa', 'cash', 'Caixa')}{btn('mais', 'dots', 'Mais')}
    </nav>
  );
}

function DrawerHost() {
  const { drawer } = useNav();
  if (!drawer) return null;
  return <Boundary resetKey={JSON.stringify(drawer)}><DrawerView d={drawer} key={JSON.stringify(drawer)} /></Boundary>;
}

function DrawerView({ d }: { d: Drawer }) {
  switch (d.kind) {
    case 'appt': return <ApptSheet id={d.id} />;
    case 'novo': return <NovoSheet {...d} />;
    case 'comanda': return <ComandaSheet id={d.id} />;
    case 'juntas': return <JuntasSheet ids={d.ids} />;
    case 'temHorario': return <TemHorarioSheet clientId={d.clientId} />;
    case 'cadeira': return <CadeiraMode id={d.id} />;
    case 'fechar': return <FecharSheet />;
    case 'mov': return <MovSheet type={d.type} />;
    case 'abrirCaixa': return <AbrirCaixaSheet />;
    case 'editarAbertura': return <EditarAberturaSheet />;
    case 'estorno': return <EstornoSheet orderId={d.orderId} />;
    case 'anamnese': return <AnamneseSheet clientId={d.clientId} />;
    case 'editCli': return <EditCliSheet clientId={d.clientId} name={d.name} />;
    case 'divida': return <DividaSheet clientId={d.clientId} />;
    case 'bloqueio': return <BloqueioSheet professionalId={d.professionalId} date={d.date} time={d.time} />;
    case 'modelos': return <ModelosSheet />;
    case 'novoPerfil': return <NovoPerfilSheet fromId={d.fromId} />;
    case 'membro': return <MembroSheet id={d.id} />;
    case 'convite': return <ConviteSheet />;
    case 'avisos': return <AvisosSheet />;
    case 'retorno': return <RetornoSheet />;
    case 'despesa': return <DespesaSheet month={d.month} />;
    case 'carrinho': return <CarrinhoSheet />;
    case 'produto': return <ProdutoSheet id={d.id} />;
    case 'pacote': return <PacoteSheet clientId={d.clientId} />;
    case 'vale': return <ValeSheet />;
    case 'juntar': return <JuntarSheet clientId={d.clientId} otherId={d.otherId} />;
    case 'instalar': return <InstalarSheet />;
    case 'fotos': return <FotosSheet clientId={d.clientId} appointmentId={d.appointmentId} />;
    default: return null;
  }
}

// tema: automático (do aparelho), claro ou escuro
initTheme();

// páginas que a cliente abre pelo link, sem login
function Public() {
  const [, kind, arg] = window.location.pathname.split('/');
  if (kind === 'agendar') return <PubBooking slug={arg || import.meta.env.VITE_SALON_SLUG || 'sublime'} />;
  if (kind === 'h' && arg) return <MeuHorario token={arg} />;
  if (kind === 'anamnese' && arg) return <AnamPub token={arg} />;
  if (kind === 'avaliar' && arg) return <Avaliar token={arg} />;
  if (kind === 'senha' && arg) return <NovaSenha token={arg} />;
  return null;
}
const isPublic = /^\/(agendar|h|anamnese|avaliar|senha)(\/|$)/.test(window.location.pathname);

registerSW();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Boundary>{isPublic ? <main><Public /></main> : <Shell />}</Boundary>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
